import React, { useState } from "react"

import BubbleCard from "../ui-components/BubbleCard"
import AlertModalWithBody from "../ui-components/AlertModalWithBody"
import RecapSection from "./RecapSection"
import CancelledCard from "./CancelledCard"
import PendingPaymentCard from "./PendingPaymentCard"
import FailedPaymentCard from "./FailedPaymentCard"
import RatingCard from "./RatingCard"

import style from "./index.module.sass"

// ----------------------------------------------------------------------------

// TODO: move to a const file
export const CASH_METHOD = "cash"

export const ORDER_STATUS = {
  PENDING_APPROVAL: "pending_approval",
  BRAND_NEW: "brand_new",
  PROCESSING: "processing",
  ACCEPTED: "accepted",
  COMPLETED: "completed",
  REJECTED: "rejected",
  PICKED_UP: "picked_up",
  CREATING: "creating",
  PENDING_PAYMENT: "pending_payment",
  PAYMENT_FAILED: "payment_failed",
}

export const PAYMENT_STATUS = {
  PENDING: "pending",
  PREAUTHORIZED: "preauthorized",
  PAID: "paid",
  FAILED: "failed",
  REFUNDED: "refunded",
  CANCELED: "canceled",
  UNKNOWN: "unknown",
  REQUIRES_AUTH: "requires_auth",
  EXPIRED: "expired",
}

export const MODE_TAKE_AWAY = "take_away"
const MODE_VENUE = "venue"

function OrderRecap(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { order = {}, userToken } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [showCovidModal, setShowCovidModal] = useState()

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function renderHeader(title, desc, isHTMLDesc = false) {
    return (
      <div className={style.header}>
        <h1 className={style.headerTitle}>{title}</h1>
        {isHTMLDesc ? (
          <p
            className={style.headerThanks}
            dangerouslySetInnerHTML={{ __html: desc }}
          ></p>
        ) : (
          <p className={style.headerThanks}>{desc}</p>
        )}
      </div>
    )
  }

  function renderWarningCard(body, color) {
    return (
      <div className={style.bubbleContainer}>
        <BubbleCard color={color}>
          <div className={style.warningCardInner}>{body}</div>
        </BubbleCard>
      </div>
    )
  }

  function renderCovidAlert() {
    return (
      <div>
        <div
          className={style.covidLink}
          onClick={() => setShowCovidModal(true)}
        >
          <i className="fal fa-person-carry" />
          {` ${I18n.t("pages.orders.show.covid.link")}`}
        </div>
        <AlertModalWithBody
          show={showCovidModal}
          onHide={() => setShowCovidModal(false)}
          onConfirm={() => setShowCovidModal(false)}
          title={I18n.t("pages.orders.show.covid.title")}
          closeLabel={I18n.t("close")}
          isTiny={false}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: I18n.t("pages.orders.show.covid.body_html"),
            }}
          ></div>
        </AlertModalWithBody>
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  let alreadyRated = order.rating && order.rating.average
  let canBeRated = order.accepts_rating == true && !(order.mode === "take_away")

  // -------------------------------------

  switch (order.status) {
    case ORDER_STATUS.PENDING_APPROVAL:
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.order_pending_approval"),
            I18n.t("pages.orders.show.order_request_received_warning")
          )}
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.PICKED_UP:
      let description =
        order.mode === MODE_VENUE
          ? I18n.t("pages.orders.show.order_picked_up_venue_delivery_html")
          : I18n.t("pages.orders.show.order_picked_up_html", {
              name: order.racer?.first_name,
              phone: order.racer?.phone_number,
            })
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.order_picked_up"),
            description,
            true
          )}
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.COMPLETED:
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.order_delivered"),
            I18n.t("pages.orders.show.thanks")
          )}
          {(alreadyRated || (!alreadyRated && canBeRated)) && userToken && (
            <RatingCard
              order={order}
              userToken={userToken}
              alreadyRated={alreadyRated}
              canBeRated={canBeRated}
            />
          )}
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.REJECTED:
      if (
        order.payment?.status === PAYMENT_STATUS.EXPIRED ||
        order.payment?.status === PAYMENT_STATUS.FAILED
      ) {
        return (
          <div className={style.container}>
            {order?.payment?.status === PAYMENT_STATUS.EXPIRED
              ? renderHeader(
                  I18n.t("pages.orders.show.payment_expired"),
                  I18n.t("pages.orders.show.payment_expired_desc")
                )
              : renderHeader(
                  I18n.t("pages.orders.show.payment_failed"),
                  I18n.t("pages.orders.show.payment_failed_desc")
                )}
            <FailedPaymentCard />
            <RecapSection order={order} />
          </div>
        )
      } else {
        return (
          <div className={style.container}>
            {renderHeader(
              I18n.t("pages.orders.show.order_refused"),
              I18n.t("pages.orders.show.order_refused_description")
            )}
            <CancelledCard />
            <RecapSection order={order} />
          </div>
        )
      }
    case ORDER_STATUS.PENDING_PAYMENT:
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.pending_payment"),
            I18n.t("pages.orders.show.pending_payment_desc")
          )}
          <PendingPaymentCard expiresAt={order?.payment?.expires_at} />
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.PAYMENT_FAILED:
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.payment_failed"),
            I18n.t("pages.orders.show.payment_failed_desc")
          )}
          <FailedPaymentCard />
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.ACCEPTED:
      return (
        <div className={style.container}>
          {order.mode === MODE_TAKE_AWAY
            ? renderHeader(
                I18n.t("pages.orders.show.order_accepted"),
                I18n.t(
                  "pages.orders.show.order_accepted_venue_takeaway_description_html",
                  {
                    time: order.start_time,
                    venue_name: order.venue?.name,
                    venue_address: `${order.venue?.address.street_name} ${order.venue?.address.street_number}`,
                  }
                ),
                true
              )
            : renderHeader(
                I18n.t("pages.orders.show.order_accepted"),
                I18n.t("pages.orders.show.order_accepted_description_html", {
                  name: order.racer?.first_name,
                  phone: order.racer?.phone_number || "--",
                }),
                true
              )}
          <RecapSection order={order} />
        </div>
      )
    case ORDER_STATUS.PROCESSING:
      return (
        <div className={style.container}>
          {renderHeader(
            I18n.t("pages.orders.show.order_request_received"),
            I18n.t("pages.orders.show.order_accepted_description_html", {
              name: order.racer?.first_name,
              phone: order.racer?.phone_number || "--",
            }),
            true
          )}
          {order.mode !== MODE_TAKE_AWAY && renderCovidAlert()}
          {order.needs_phone_verification &&
            renderWarningCard(
              I18n.t("pages.orders.show.keep_your_phone_close"),
              "yellow"
            )}
          {order.credit_card &&
            order.payment.status === PAYMENT_STATUS.PREAUTHORIZED &&
            renderWarningCard(
              I18n.t("pages.orders.show.stripe.preauthorized_payment"),
              "blue"
            )}
          {order.credit_card &&
            order.payment.status === PAYMENT_STATUS.PAID &&
            renderWarningCard(
              I18n.t("pages.orders.show.stripe.paid_payment", {
                price: order.payment.stripe_paid_amount,
              }),
              "blue"
            )}
          <RecapSection order={order} />
        </div>
      )
    default:
      // BRAND_NEW
      return (
        <div className={style.container}>
          {order.mode === MODE_TAKE_AWAY
            ? renderHeader(
                I18n.t("pages.orders.show.order_request_takeaway"),
                I18n.t("pages.orders.show.order_request_takeaway_warning")
              )
            : renderHeader(
                I18n.t("pages.orders.show.order_request_received"),
                I18n.t("pages.orders.show.order_request_received_warning")
              )}
          {order.mode !== MODE_TAKE_AWAY && renderCovidAlert()}
          {order.needs_phone_verification &&
            renderWarningCard(
              I18n.t("pages.orders.show.keep_your_phone_close"),
              "yellow"
            )}
          {order.credit_card &&
            order.payment.status === PAYMENT_STATUS.PREAUTHORIZED &&
            renderWarningCard(
              I18n.t("pages.orders.show.stripe.preauthorized_payment"),
              "blue"
            )}
          {order.credit_card &&
            order.payment.status === PAYMENT_STATUS.PAID &&
            renderWarningCard(
              I18n.t("pages.orders.show.stripe.paid_payment", {
                price: order.payment.stripe_paid_amount,
              }),
              "blue"
            )}
          <RecapSection order={order} />
        </div>
      )
  }
}

// ----------------------------------------------------------------------------

export default OrderRecap
